import { Controller } from "@hotwired/stimulus";

// SAL-047: the sales drawers (the News reader, the lead drawer) as the
// prototypes have them. The drawer lives in a Turbo frame over the list; this
// controller sits on the frame's host and:
// - locks the page scroll while a drawer is open and moves focus into it,
// - closes it on Escape or a click on the scrim (it follows the drawer's own
//   Close link, a frame visit that advances the URL, so the list stays put),
// - keeps Tab inside the drawer,
// - gives focus back to the row or card that opened it.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default class extends Controller {
  // Turbo can swap the frame element on an advancing frame visit, so a
  // listener on it goes stale; watching the host for the drawer panel
  // appearing, changing or leaving does not.
  connect() {
    this.onKey = (event) => this.keydown(event);
    this.onClick = (event) => this.remember(event);
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.element, { childList: true, subtree: true });
    document.addEventListener("keydown", this.onKey);
    document.addEventListener("click", this.onClick, true);
    this.sync();
  }

  disconnect() {
    this.observer.disconnect();
    document.removeEventListener("keydown", this.onKey);
    document.removeEventListener("click", this.onClick, true);
    document.documentElement.classList.remove("sales-drawer-open");
  }

  get frame() {
    return this.element.querySelector("turbo-frame");
  }

  get panel() {
    return this.element.querySelector(".sales-drawer");
  }

  // The link that opens a drawer is outside the frame; keep it to return to.
  remember(event) {
    const frame = this.frame;
    if (!frame || this.panel) return;
    const link = event.target.closest(`a[data-turbo-frame="${frame.id}"]`);
    if (link && !frame.contains(link)) this.opener = link;
  }

  // Acts only when the panel itself changes: a new drawer (or another version
  // in it) takes focus to Close; the drawer leaving hands focus back.
  sync() {
    const panel = this.panel;
    if (panel === this.current) return;
    this.current = panel;
    this.mark(panel);
    if (panel) {
      document.documentElement.classList.add("sales-drawer-open");
      const close = panel.querySelector("[data-sales-drawer-close]");
      (close || panel).focus({ preventScroll: true });
    } else {
      document.documentElement.classList.remove("sales-drawer-open");
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }
  }

  // SAL-049: a piece drawer names its card and the related ones; ring those
  // on the Board and dim the rest while it is open.
  mark(panel) {
    const selected = panel && panel.dataset.salesSelected;
    let related = [];
    try { related = selected ? JSON.parse(panel.dataset.salesRelated || "[]") : []; } catch (_e) { related = []; }
    document.querySelectorAll(".sales-board").forEach((board) => board.classList.toggle("has-sel", Boolean(selected)));
    document.querySelectorAll("[data-piece-code]").forEach((card) => {
      const code = card.dataset.pieceCode;
      card.classList.toggle("is-sel", code === selected);
      card.classList.toggle("is-rel", related.includes(code));
    });
  }

  close(event) {
    if (event) event.preventDefault();
    const close = this.panel && this.panel.querySelector("[data-sales-drawer-close]");
    if (close) close.click();
  }

  keydown(event) {
    const panel = this.panel;
    if (!panel) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== "Tab") return;

    const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!panel.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
